// client/src/routes/app/tenants/$tenantId/edit.tsx — SCR-TEN-002 (edit) · TEN-004.
//
// Nested under `$tenantId` rather than beside it, so the URL says which tenant is being edited
// and a refresh lands back on the same form. It is NOT a tab: the tabs are readings of the
// tenant and this is a write, and putting an editor among them would make „Profil" ambiguous.
//
// Two flags travel from the detail into the form, and both are the server's word rather than
// this screen's inference: `canChangeTaxpayerType` (05 TEN-001 criterion 2 — the withholding
// switch is frozen while a contract is ACTIVE; D-374, shipped by TEN-001 as its D-571) and
// `hasCnp`, which decides whether the disclosure's hint says „already on file" or „not
// recorded" (D-373, TEN-001's D-572). `PUT /api/v1/tenants/{id}` takes the whole profile, and
// `cnp: null` on it means keep what is stored.

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import { PageHeader } from '@/components/layout/PageHeader';
import { RequirePermission } from '@/components/layout/route-guards';
import { ErrorState, LoadingState } from '@/components/ui/states';
import { TenantForm } from '@/features/tenants/TenantForm';
import { useTenant, useUpdateTenant } from '@/features/tenants/queries';
import { toTenantForm, toTenantProfileInput, type TenantFormValues } from '@/features/tenants/schema';
import { toast } from '@/stores/toast-store';

export const Route = createFileRoute('/app/tenants/$tenantId/edit')({
  component: EditTenantScreen,
});

function EditTenantScreen() {
  const { tenantId } = Route.useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const tenant = useTenant(tenantId);
  const update = useUpdateTenant();

  if (tenant.isPending) return <LoadingState rows={4} />;
  if (tenant.isError) return <ErrorState error={tenant.error} onRetry={() => void tenant.refetch()} />;

  const back = async () => {
    await navigate({ to: '/app/tenants/$tenantId', params: { tenantId } });
  };

  return (
    <RequirePermission permission="Leasing.Write">
      <PageHeader
        title={t('tenants.form.editTitle')}
        description={tenant.data.displayName}
      />
      <TenantForm
        initialValues={toTenantForm(tenant.data)}
        submitLabel={t('common.save')}
        canChangeTaxpayerType={tenant.data.canChangeTaxpayerType}
        hasCnp={tenant.data.hasCnp}
        onSubmit={async (values: TenantFormValues) => {
          await update.mutateAsync({ path: { id: tenantId }, body: toTenantProfileInput(values) });
          toast.success(t('tenants.form.saved'));
          await back();
        }}
        onCancel={() => {
          void back();
        }}
      />
    </RequirePermission>
  );
}
